import { useCallback, useRef, useState } from 'react'

/**
 * Measure the natural (unclamped) width of a header control so it can be used as
 * a live minimum-width floor for the column that hosts it.
 *
 * Used for the first column's bulk-select + "Selected (N)" + Actions control:
 * the floor must grow with the rendered control (e.g. when the selected count
 * widens from "(9)" to "(1,284)" or a bulk Actions button is added) so it never
 * gets clipped or overflows into the next cell.
 *
 * Implemented with a callback ref + ResizeObserver rather than useEffect (per the
 * client no-useEffect rule): the observer is wired up the moment the node mounts
 * and torn down when it unmounts.
 *
 * The measured node should be allowed to size to its content (e.g. an
 * `inline-flex` / `w-max` wrapper) so `scrollWidth` reflects the true required
 * width even while the surrounding cell is narrower.
 *
 * @param extraPadding px added to the measured width (cell padding, safety gap).
 * @returns `[floor, measureRef]` — attach `measureRef` to the content wrapper.
 */
export function useMeasuredFloor(
  extraPadding = 0
): [number, (node: HTMLElement | null) => void] {
  const [floor, setFloor] = useState(0)
  const observerRef = useRef<ResizeObserver | null>(null)

  const measureRef = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null

      if (!node) return

      const measure = () => {
        // scrollWidth = natural content width even when the cell clamps it.
        const width = Math.ceil(node.scrollWidth) + extraPadding
        setFloor((prev) => (prev === width ? prev : width))
      }

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(measure)
        observer.observe(node)
        observerRef.current = observer
      }
      measure()
    },
    [extraPadding]
  )

  return [floor, measureRef]
}
